import React, { useCallback, useEffect, useState, Suspense, lazy } from 'react';

import Spinner from 'react-bootstrap/Spinner';
import { isTablet, isMobileOnly, useMobileOrientation } from 'react-device-detect';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';

import { isEmpty, sumOff } from '_dash';
import { INDEX_ROUTE, SIGNUP_ROUTE } from 'appRoutes/routeConstants';
import { getProfileRoute } from 'appRoutes/routeHelpers';
import { AUTHORIZATION_KEY, SHOP_SERVICE_TYPE, MOBILE_SERVICE_TYPE } from 'global_constants';
import useLocalStorage from 'hooks/localstorage';
import AccountHook from 'hooks/account';
import BarberProfilesHook from 'hooks/barberProfiles';
import ProfileMetaHook from 'hooks/profileMeta';
import { getCalloutDisplayAddress } from 'utils';
import { barberShopViewed, getBarberBookingDetailByReviewKey } from 'actions';
import AppWebHeader from 'components/appWebHeader';

import ProfileWork from './components/work';
import WebProfileLocation from './web/location';
import MobileProfileNavBar from './mobile/navBar';
import WebProfileBasicDetail from './web/basicDetail';
import MobileProfileBasicDetail from './mobile/basicDetail';
import { SignUpReviewModalConsumer } from '../../../context/reviewModalAuth';
import { setReviewSignUpBool as signUpReviewSetter } from '../../../reducers/reviewModalAuthReducer';
import { eventTrackingApiHandler } from 'actions/eventTracking';
import { getUserSessionUUID, isProfileEventAdded, saveProfileEvent } from 'utils/user-events';

const ProfileServices = lazy(() => import('./components/services'));
const ProfileSchedule = lazy(() => import('./components/schedule'));
const ProfileReviews = lazy(() => import('./components/reviews'));
const ProfileFooter = lazy(() => import('./components/footer'));
const ProfileCart = lazy(() => import('./components/cart'));
const ReviewModal = lazy(() => import('./components/review-modal'));

const BarberProfile = ({ updateMeta, accountData, setAccountData, getBarberProfile }) => {
  const params = useParams();
  const history = useHistory();
  const reviewKey = params.k;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const rvParam = queryParams.get('rv');

  const isLoggedIn = !isEmpty(accountData);
  const [signUpReviewModalConsumer, dispatch] = SignUpReviewModalConsumer();
  const signUpReview = signUpReviewModalConsumer.signUpReview || [];

  const isPortrait = useMobileOrientation().isPortrait;
  const isMobile = isMobileOnly || (isTablet && isPortrait);
  const [barbersCart, setBarbersCart] = useLocalStorage('cart', {});

  const [username, setUsername] = useState('');
  const [address, setAddress] = useState({});
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [barberDetail, setBarberDetail] = useState({});
  const [bookingDetail, setBookingDetail] = useState({});
  const [clientReferral, setClientReferral] = useState({});
  const [selectedServices, setSelectedServices] = useState([]);
  const [filteredServices] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState(SHOP_SERVICE_TYPE);
  const [visible, setVisible] = useState(false);
  const [reviewStep, setReviewStep] = useState(1);

  useEffect(() => {
    if (params?.username) {
      setUsername(params.username);
      setBarberDetail({});
    }
  }, [params?.username]);

  const setInitialServiceType = useCallback(
    (detail) => {
      if (!detail.shop) {
        if (detail.is_shop && detail.is_mobile) {
          const cart = barbersCart[username] || {};
          const shopSchedulesExist = selectedServiceType === 'shop' && (detail.schedules || []).filter((item) => item.schedule_type === 'shop');
          const shopSchedulesAndServicesExist = selectedServiceType === 'shop' && (detail.services || []).filter((item) => item.location_type === 'shop');
          if (shopSchedulesExist.length > 0 || shopSchedulesAndServicesExist.length > 0) {
            setSelectedServiceType(cart.service_type || selectedServiceType);
          } else {
            setSelectedServiceType(MOBILE_SERVICE_TYPE);
          }
        } else {
          if (detail.is_shop) {
            setSelectedServiceType(SHOP_SERVICE_TYPE);
          } else {
            setSelectedServiceType(MOBILE_SERVICE_TYPE);
          }
        }
      }
    },
    [barbersCart, selectedServiceType, username]
  );

  useEffect(() => {
    if (barberDetail?.id) {
      setClientReferral(barberDetail.client_referral || {});
      // Only check cart if barber object is a barber profile, not a shop
      if (barberDetail?.barber) {
        setInitialServiceType(barberDetail);
        const { services = [] } = barberDetail;
        const cart = barbersCart[username === 'signup' || username === 'login' ? signUpReview.username : username] || {};
        const cartServiceExist = [];
        if (cart && cart.services) {
          for (const id of cart.services) {
            const matchingService = barberDetail.services.find((service) => service.id === id);
            if (matchingService) {
              if (
                (cart.service_type === 'shop' && barberDetail.is_shop) ||
                (cart.service_type === 'mobile' && barberDetail.is_mobile) ||
                (cart.service_type === 'custom' && barberDetail.is_shop)
              ) {
                cartServiceExist.push(id);
              }
            }
          }
        }
        if (cartServiceExist.length > 0) {
          setSelectedServices(services.filter((item) => (cart.services || []).includes(item.id)));
        }
      }
    }
    return () => {
      setSelectedServices([]);
    };
  }, [barberDetail, barbersCart, setInitialServiceType, signUpReview.username, username]);

  useEffect(() => {
    if (username) {
      if (barberDetail?.id) {
        return;
      }

      let currentUsername = username;
      if (username === 'signup' || username === 'login') {
        currentUsername = signUpReview.username ?? location?.state?.username;
      }
      getBarberProfile(currentUsername)
        .then((response) => {
          setLoading(false);
          setBarberDetail(response);
          updateMeta(response);
        })
        .catch((_err) => {
          history.push(INDEX_ROUTE);
        });
    }
  }, [barberDetail?.id, getBarberProfile, history, location?.state?.username, signUpReview.username, updateMeta, username]);

  useEffect(() => {
    if (barberDetail && Object.keys(barberDetail).length > 1) {
      if (rvParam || signUpReview?.rvParam) {
        setVisible(true);
        if (signUpReview?.signUpBool) {
          if (isLoggedIn) {
            setReviewStep(2);
          } else {
            setReviewStep(1);
          }
        } else {
          setReviewStep(1);
        }
      } else {
        setVisible(false);
      }
    }
  }, [barberDetail, isLoggedIn, rvParam, signUpReview?.rvParam, signUpReview?.signUpBool]);

  useEffect(() => {
    if (username && barberDetail?.id) {
      if (barberDetail.shop) {
        barberShopViewed(username);
      } else {
        const visitor_id = getUserSessionUUID();
        const isEventAdded = isProfileEventAdded(visitor_id, username);
        if (!isEventAdded) {
          const payload = {
            barber_profile: barberDetail.id,
            event_type: 'page_visit',
            event_details: {
              page_name: 'home_page',
            },
            visitor_id,
          };
          if (accountData?.id) {
            payload.user_id = accountData?.id;
          }
          eventTrackingApiHandler(payload);
          saveProfileEvent(visitor_id, username);
        }
      }
    }
  }, [accountData.id, barberDetail?.shop, barberDetail.id, username]);

  useEffect(() => {
    if (reviewKey && barberDetail && Object.keys(barberDetail).length > 0) {
      const params = new URLSearchParams({
        expand: 'barber',
        k: reviewKey,
      }).toString();
      getBarberBookingDetailByReviewKey(params).then((response) => {
        const { data } = response;
        data.review_key = reviewKey;
        setVisible(true);
        setBookingDetail(data);
        setReviewStep(2);
      });
    }
  }, [reviewKey, barberDetail]);

  useEffect(() => {
    setSelectedServices([]);
  }, [selectedServiceType]);

  useEffect(() => {
    if ((barberDetail?.schedules || []).length) {
      if (barberDetail.shop) {
        setSchedules(barberDetail.schedules || []);
      } else {
        const schedulesShop = (barberDetail.schedules || []).filter((item) => item.schedule_type === selectedServiceType);
        const shopSchedulesAndServicesExist =
          selectedServiceType === 'shop' && (barberDetail.services || []).filter((item) => item.location_type === selectedServiceType);
        if (schedulesShop.length > 0 || shopSchedulesAndServicesExist.length > 0) {
          setSchedules(schedulesShop);
        } else {
          const schedulesMobile = (barberDetail.schedules || []).filter((item) => item.schedule_type === 'mobile');
          setSchedules(schedulesMobile);
        }
      }
    }
  }, [barberDetail.schedules, barberDetail.services, barberDetail.shop, selectedServiceType]);

  useEffect(() => {
    if (!isEmpty(barberDetail)) {
      const { addresses = [] } = barberDetail;
      let address = addresses[0];
      const shopSchedulesExist = selectedServiceType === 'shop' && (barberDetail.schedules || []).filter((item) => item.schedule_type === 'shop');
      const shopSchedulesAndServicesExist = selectedServiceType === 'shop' && (barberDetail.services || []).filter((item) => item.location_type === 'shop');
      if (!barberDetail.shop) {
        if (shopSchedulesExist.length > 0 || shopSchedulesAndServicesExist.length > 0) {
          address = addresses.find((item) => item.address_type === selectedServiceType);
        } else {
          address = addresses.find((item) => item.address_type === 'mobile');
        }
        if (address) {
          address.display_name = getCalloutDisplayAddress(address.place_detail);
        }
      }
      setAddress(address || {});
    }
  }, [barberDetail, selectedServiceType]);

  const updateLocalCart = useCallback(
    (services) => {
      const cartIndex = Object.keys(barbersCart).indexOf(username);
      if (cartIndex === -1) {
        barbersCart[username] = {};
      }
      barbersCart[username] = {
        slot_day: null,
        service_type: selectedServiceType,
        services: services.map((i) => i.id),
        total_service_time: sumOff(services, 'duration'),
      };
      setBarbersCart(barbersCart);
    },
    [barbersCart, selectedServiceType, setBarbersCart, username]
  );

  const handleServiceChange = useCallback(
    (service) => {
      const index = selectedServices.findIndex((item) => item.id === service.id);
      if (index > -1) {
        selectedServices.splice(index, 1);
      } else {
        selectedServices.push(service);
      }
      setSelectedServices([...selectedServices]);
      updateLocalCart(selectedServices);
    },
    [selectedServices, updateLocalCart]
  );

  const setSignUpReviewBoolHandler = useCallback(
    (data) => {
      dispatch(signUpReviewSetter(data));
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    setAccountData({});
    localStorage.removeItem(AUTHORIZATION_KEY);
    localStorage.clear();
  }, [setAccountData]);

  const reload = useCallback(() => {
    history.push(location.pathname);
    history.go(0);
  }, [history, location.pathname]);

  const onModalClose = useCallback(() => {
    setVisible(false);
    if (bookingDetail) {
      history.push(getProfileRoute(username));
    } else {
      setSignUpReviewBoolHandler({
        signUpBool: false,
        rvParam: null,
        username: null,
      });
      reload();
    }
    setBookingDetail(undefined);
  }, [bookingDetail, history, reload, setSignUpReviewBoolHandler, username]);

  const onModalDropBackClose = useCallback(() => {
    setVisible(false);
    setSignUpReviewBoolHandler(undefined);
  }, [setSignUpReviewBoolHandler]);

  const handleOnSignup = useCallback(() => {
    setSignUpReviewBoolHandler({
      signUpBool: true,
      rvParam: rvParam,
      username: username,
    });
    history.push(SIGNUP_ROUTE);
  }, [history, rvParam, setSignUpReviewBoolHandler, username]);

  const handleServiceTypeChange = useCallback(
    (value) => {
      setSelectedServiceType(value);
      const cartIndex = Object.keys(barbersCart).indexOf(username);
      if (cartIndex === -1) {
        barbersCart[username] = {};
      }
      barbersCart[username] = {
        slot_day: null,
        service_type: value,
        services: [],
        total_service_time: sumOff([], 'duration'),
      };
      setBarbersCart(barbersCart);
    },
    [barbersCart, setBarbersCart, username]
  );

  const onBarberChange = useCallback(
    (item) => {
      history.push(getProfileRoute(item.username));
    },
    [history]
  );

  if (loading || !barberDetail?.id) {
    return (
      <div className='app-loader'>
        <Spinner animation='border' />
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <div className='barber-profile'>
          <MobileProfileNavBar detail={barberDetail} isLoggedIn={isLoggedIn} accountData={accountData} services={selectedServices} onLogout={logout} />
          <div className='profile-body'>
            <MobileProfileBasicDetail address={address} detail={barberDetail} schedules={schedules} isLoggedIn={isLoggedIn} referrral={clientReferral} />
            <ProfileWork detail={barberDetail} isMobile />
            <Suspense fallback={<></>}>
              <ProfileReviews isMobile username={username} detail={barberDetail} />
              <ProfileServices
                detail={barberDetail}
                services={filteredServices}
                selectedServices={selectedServices}
                selectedLocationType={selectedServiceType}
                onServiceChange={handleServiceChange}
                onLocationTypeChange={handleServiceTypeChange}
                hasBothServices={barberDetail.is_shop && barberDetail.is_mobile}
                onBarberChange={onBarberChange}
              />
            </Suspense>
          </div>
          <Suspense fallback={<></>}>
            <ProfileFooter isMobile={isMobile} whatsappLink={barberDetail?.whatsapp_link} displayName={barberDetail?.display_name} />
          </Suspense>
        </div>
        <If condition={selectedServices.length && barberDetail?.barber}>
          <Suspense fallback={<></>}>
            <ProfileCart detail={barberDetail} isLoggedIn={isLoggedIn} services={selectedServices} customerId={accountData.id} />
          </Suspense>
        </If>

        <Suspense fallback={<></>}>
          <ReviewModal
            visible={visible}
            isLoggedIn={isLoggedIn}
            reviewStep={reviewStep}
            barberDetail={barberDetail}
            bookingDetail={bookingDetail}
            accountData={accountData}
            setReviewStep={setReviewStep}
            handleOnSignup={handleOnSignup}
            onModalClose={onModalClose}
            onModalDropBackClose={onModalDropBackClose}
          />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <div className='barber-profile'>
        <AppWebHeader redirectOnLogout={false} />
        <div className='profile-body'>
          <Row className='profile-row'>
            <Col lg={4}>
              <WebProfileBasicDetail detail={barberDetail} />
            </Col>
            <Col lg={8}>
              <ProfileWork detail={barberDetail} />
            </Col>
          </Row>
          <Row className='profile-row'>
            <Col lg={6}>
              <WebProfileLocation detail={barberDetail} address={address} />
              <Suspense fallback={<></>}>
                <ProfileServices
                  detail={barberDetail}
                  selectedServices={selectedServices}
                  selectedLocationType={selectedServiceType}
                  onServiceChange={handleServiceChange}
                  hasBothServices={barberDetail.is_shop && barberDetail.is_mobile}
                  onLocationTypeChange={handleServiceTypeChange}
                  onBarberChange={onBarberChange}
                />
              </Suspense>
            </Col>
            <Col lg={6}>
              <div className='mb-24'>
                <p className='text-3xl semi-bold mb-16'>Opening Hours</p>
                <Card>
                  <Suspense fallback={<></>}>
                    <ProfileSchedule schedules={schedules} />
                  </Suspense>
                </Card>
              </div>
              <Suspense fallback={<></>}>
                <ProfileReviews username={username} detail={barberDetail} />
              </Suspense>
            </Col>
          </Row>
        </div>
        <Suspense fallback={<></>}>
          <ProfileFooter isMobile={isMobile} whatsappLink={barberDetail?.whatsapp_link} displayName={barberDetail?.display_name} />
        </Suspense>
      </div>

      <If condition={selectedServices.length && barberDetail?.barber}>
        <Suspense fallback={<></>}>
          <ProfileCart detail={barberDetail} isLoggedIn={isLoggedIn} services={selectedServices} customerId={accountData.id} />
        </Suspense>
      </If>

      <Suspense fallback={<></>}>
        <ReviewModal
          visible={visible}
          isLoggedIn={isLoggedIn}
          reviewStep={reviewStep}
          barberDetail={barberDetail}
          bookingDetail={bookingDetail}
          accountData={accountData}
          setReviewStep={setReviewStep}
          handleOnSignup={handleOnSignup}
          onModalClose={onModalClose}
          onModalDropBackClose={onModalDropBackClose}
        />
      </Suspense>
    </>
  );
};

export default React.memo(BarberProfilesHook(AccountHook(ProfileMetaHook(BarberProfile))));
